import { useState, useEffect, useRef, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Clock,
  Users,
  UserPlus,
  Search,
  CalendarIcon,
  ArrowLeft,
  Camera,
  MapPin,
  Trash2,
  Mail,
  Settings,
  Shield,
  Key,
  Edit,
  LogOut,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Service Pro Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_f6c01219-7e39-4c2c-8dd7-836e4f15723a/artifacts/1iort7xw_Image%20%281%29%20-%20Edited%20%281%29.png";

// Helper function to format dates to Eastern Time
const formatToEasternTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

// Employee Clock In/Out Page
const EmployeePage = () => {
  const [pin, setPin] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [clockAction, setClockAction] = useState(null);
  const [location, setLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState("pending"); // pending, granted, denied, unavailable
  const [cameraReady, setCameraReady] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const getLocation = () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        setLocationStatus("unavailable");
        resolve(null);
        return;
      }
      
      setLocationStatus("pending");
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocationStatus("granted");
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.log("Location error:", error.code, error.message);
          if (error.code === 1) {
            setLocationStatus("denied");
          } else {
            setLocationStatus("unavailable");
          }
          resolve(null);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });
  };

  // Request location permission on page load
  useEffect(() => {
    // Check if we can get location
    if (navigator.geolocation) {
      navigator.permissions?.query({ name: 'geolocation' }).then((result) => {
        if (result.state === 'granted') {
          setLocationStatus("granted");
        } else if (result.state === 'denied') {
          setLocationStatus("denied");
        } else {
          setLocationStatus("pending");
        }
      }).catch(() => {
        // permissions API not supported, will request on action
        setLocationStatus("pending");
      });
    } else {
      setLocationStatus("unavailable");
    }
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } } 
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraReady(true);
        };
      }
    } catch (error) {
      console.error("Camera error:", error);
      toast.error("Could not access camera. Proceeding without photo.");
      setCameraReady(true);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && videoRef.current.videoWidth > 0) {
      const canvas = document.createElement("canvas");
      canvas.width = videoRef.current.videoWidth;
      canvas.height = videoRef.current.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(videoRef.current, 0, 0);
      return canvas.toDataURL("image/jpeg", 0.7);
    }
    return null;
  };

  const handleClockAction = async (action) => {
    if (!pin || pin.length < 4) {
      toast.error("Please enter a valid PIN");
      return;
    }

    setClockAction(action);
    
    // Request location FIRST before showing camera
    toast.info("Requesting location...");
    const loc = await getLocation();
    setLocation(loc);
    
    if (!loc) {
      toast.warning("Location not available. Please enable location services.");
    }
    
    // Then show camera dialog
    setShowCamera(true);
  };

  useEffect(() => {
    if (showCamera) {
      startCamera();
    }
    return () => stopCamera();
  }, [showCamera]);

  const completeClockAction = async (photo) => {
    setIsProcessing(true);
    setShowCamera(false);
    stopCamera();

    try {
      const endpoint = clockAction === "in" ? "/clock/in" : "/clock/out";
      const response = await axios.post(`${API}${endpoint}`, {
        pin,
        photo,
        location,
      });

      toast.success(response.data.message);
      setPin("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error processing request");
    } finally {
      setIsProcessing(false);
      setClockAction(null);
      setLocation(null);
    }
  };

  const handleCapture = () => {
    const photo = capturePhoto();
    completeClockAction(photo);
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4">
      <img src={LOGO_URL} alt="Service Pro" className="h-24 mb-6" />
      
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold flex items-center justify-center gap-2">
            <Clock className="h-8 w-8 text-blue-600" />
            Time Clock
          </CardTitle>
          <p className="text-gray-500">Enter your PIN to clock in or out</p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="pin" className="text-lg">Employee PIN</Label>
            {/* PIN Display */}
            <div className="flex justify-center items-center gap-2 mt-3 mb-4">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className={`w-10 h-12 rounded-lg border-2 flex items-center justify-center text-2xl font-bold
                    ${pin[i] ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'}`}
                >
                  {pin[i] ? '●' : ''}
                </div>
              ))}
            </div>
            
            {/* Custom Number Pad */}
            <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button
                  key={num}
                  type="button"
                  onClick={() => pin.length < 6 && setPin(pin + num)}
                  className="h-16 text-2xl font-semibold rounded-xl bg-white border-2 border-gray-200 
                    hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all shadow-sm"
                >
                  {num}
                </button>
              ))}
              {/* Clear button */}
              <button
                type="button"
                onClick={() => setPin('')}
                className="h-16 text-sm font-medium rounded-xl bg-gray-100 border-2 border-gray-200 
                  hover:bg-gray-200 active:bg-gray-300 active:scale-95 transition-all text-gray-600"
              >
                Clear
              </button>
              {/* 0 button */}
              <button
                type="button"
                onClick={() => pin.length < 6 && setPin(pin + '0')}
                className="h-16 text-2xl font-semibold rounded-xl bg-white border-2 border-gray-200 
                  hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all shadow-sm"
              >
                0
              </button>
              {/* Backspace button */}
              <button
                type="button"
                onClick={() => setPin(pin.slice(0, -1))}
                className="h-16 text-xl font-medium rounded-xl bg-gray-100 border-2 border-gray-200 
                  hover:bg-gray-200 active:bg-gray-300 active:scale-95 transition-all text-gray-600"
              >
                ⌫
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-6">
            <Button
              size="lg"
              className="bg-green-600 hover:bg-green-700 h-16 text-lg"
              onClick={() => handleClockAction("in")}
              disabled={isProcessing || pin.length < 4}
              data-testid="clock-in-btn"
            >
              <Clock className="mr-2 h-5 w-5" />
              Clock In
            </Button>
            <Button
              size="lg"
              className="bg-red-600 hover:bg-red-700 h-16 text-lg"
              onClick={() => handleClockAction("out")}
              disabled={isProcessing || pin.length < 4}
              data-testid="clock-out-btn"
            >
              <Clock className="mr-2 h-5 w-5" />
              Clock Out
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showCamera} onOpenChange={(open) => {
        if (!open) {
          stopCamera();
          setShowCamera(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Capture Photo
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative rounded-lg overflow-hidden bg-black">
              <video ref={videoRef} autoPlay playsInline muted className="w-full" />
              {!cameraReady && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                  <div className="text-white">Loading camera...</div>
                </div>
              )}
            </div>
            {location && (
              <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded">
                <MapPin className="h-4 w-4" />
                ✓ Location captured successfully
              </div>
            )}
            {!location && locationStatus === "denied" && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                <MapPin className="h-4 w-4" />
                ✗ Location permission denied. Please enable in browser/device settings and refresh.
              </div>
            )}
            {!location && locationStatus !== "denied" && (
              <div className="flex items-center gap-2 text-sm text-orange-500 bg-orange-50 p-2 rounded">
                <MapPin className="h-4 w-4" />
                ⚠ Location not available. Check that location services are enabled.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { stopCamera(); setShowCamera(false); }}>
              Cancel
            </Button>
            <Button onClick={handleCapture} disabled={!cameraReady} data-testid="capture-photo-btn">
              <Camera className="mr-2 h-4 w-4" />
              {cameraReady ? `Capture & ${clockAction === "in" ? "Clock In" : "Clock Out"}` : "Waiting for camera..."}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toaster position="top-center" />
    </div>
  );
};

// Admin Login
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async () => {
    if (!username || !password) {
      toast.error("Please enter username and password");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      onLogin(response.data.admin);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center p-4">
      <img src={LOGO_URL} alt="Service Pro" className="h-24 mb-6" />
      
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Admin Login</CardTitle>
          <p className="text-gray-500">Enter your credentials to continue</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="admin-username">Username</Label>
            <Input
              id="admin-username"
              placeholder="Enter username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="mt-2"
              data-testid="admin-username-input"
            />
          </div>
          <div>
            <Label htmlFor="admin-password">Password</Label>
            <Input
              id="admin-password"
              type="password"
              placeholder="Enter password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2"
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              data-testid="admin-password-input"
            />
          </div>
          <Button
            className="w-full"
            onClick={handleLogin}
            disabled={isLoading}
            data-testid="admin-login-btn"
          >
            {isLoading ? "Logging in..." : "Login"}
          </Button>
        </CardContent>
      </Card>
      <Toaster position="top-center" />
    </div>
  );
};

// Settings Dialog Component
const SettingsDialog = ({ open, onOpenChange, currentAdmin, onAdminUpdate }) => {
  const [admins, setAdmins] = useState([]);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showEditAdmin, setShowEditAdmin] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: "", password: "", email: "", role: "manager" });
  const [passwordData, setPasswordData] = useState({ current: "", new: "", confirm: "" });
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editAdminData, setEditAdminData] = useState({ username: "", email: "" });
  const [managerPermissions, setManagerPermissions] = useState({
    can_view_all: true,
    can_add_employees: false,
    can_edit_employees: false,
    can_delete_employees: false,
  });

  const isPresident = currentAdmin?.role === "president";

  useEffect(() => {
    if (open && isPresident) {
      fetchAdmins();
    }
  }, [open, isPresident]);

  const fetchAdmins = async () => {
    try {
      const response = await axios.get(`${API}/admins`);
      setAdmins(response.data);
    } catch (error) {
      console.error("Failed to fetch admins");
    }
  };

  const handleAddAdmin = async () => {
    try {
      const payload = {
        ...newAdmin,
        permissions: newAdmin.role === "manager" ? managerPermissions : undefined
      };
      await axios.post(`${API}/admins`, payload);
      toast.success("Admin created successfully");
      setShowAddAdmin(false);
      setNewAdmin({ username: "", password: "", email: "", role: "manager" });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create admin");
    }
  };

  const handleEditAdmin = (admin) => {
    setEditingAdmin(admin);
    setEditAdminData({ username: admin.username, email: admin.email || "" });
    setShowEditAdmin(true);
  };

  const handleSaveAdminEdit = async () => {
    try {
      await axios.put(`${API}/admins/${editingAdmin.id}`, editAdminData);
      toast.success("Admin updated successfully");
      setShowEditAdmin(false);
      fetchAdmins();
      // If editing current admin, update the parent
      if (editingAdmin.id === currentAdmin.id && onAdminUpdate) {
        onAdminUpdate({ ...currentAdmin, ...editAdminData });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update admin");
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new !== passwordData.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    try {
      await axios.put(`${API}/admins/${currentAdmin.id}/password`, {
        current_password: passwordData.current,
        new_password: passwordData.new
      });
      toast.success("Password changed successfully");
      setShowChangePassword(false);
      setPasswordData({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm("Are you sure you want to delete this admin?")) return;
    try {
      await axios.delete(`${API}/admins/${adminId}`);
      toast.success("Admin deleted successfully");
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete admin");
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "president": return "bg-purple-100 text-purple-800";
      case "vice_president": return "bg-blue-100 text-blue-800";
      case "manager": return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "president": return "President";
      case "vice_president": return "Vice President";
      case "manager": return "Manager";
      default: return role;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Settings
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Current Admin Info */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Logged in as</p>
                <p className="font-semibold">{currentAdmin?.username}</p>
                <span className={`inline-block px-2 py-1 text-xs rounded mt-1 ${getRoleBadgeColor(currentAdmin?.role)}`}>
                  {getRoleLabel(currentAdmin?.role)}
                </span>
              </div>
              <Button variant="outline" onClick={() => setShowChangePassword(true)}>
                <Key className="mr-2 h-4 w-4" />
                Change Password
              </Button>
            </div>
          </div>

          {/* Admin Management (President Only) */}
          {isPresident && (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Admin Management
                </h3>
                <Button onClick={() => setShowAddAdmin(true)}>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Add Admin
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Username</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {admins.map((admin) => (
                    <TableRow key={admin.id}>
                      <TableCell className="font-medium">{admin.username}</TableCell>
                      <TableCell>{admin.email || "-"}</TableCell>
                      <TableCell>
                        <span className={`inline-block px-2 py-1 text-xs rounded ${getRoleBadgeColor(admin.role)}`}>
                          {getRoleLabel(admin.role)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEditAdmin(admin)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {admin.role !== "president" && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={() => handleDeleteAdmin(admin.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Add Admin Dialog */}
        <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Admin</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Username</Label>
                <Input
                  value={newAdmin.username}
                  onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                  placeholder="Enter username"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  placeholder="Enter password"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  placeholder="Enter email (for alerts)"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={newAdmin.role} onValueChange={(val) => setNewAdmin({ ...newAdmin, role: val })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vice_president">Vice President</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newAdmin.role === "manager" && (
                <div className="space-y-2 p-4 bg-gray-50 rounded-lg">
                  <Label>Manager Permissions</Label>
                  <div className="space-y-2 mt-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={managerPermissions.can_view_all}
                        onCheckedChange={(checked) => setManagerPermissions({ ...managerPermissions, can_view_all: checked })}
                      />
                      <label className="text-sm">Can view all employees</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={managerPermissions.can_add_employees}
                        onCheckedChange={(checked) => setManagerPermissions({ ...managerPermissions, can_add_employees: checked })}
                      />
                      <label className="text-sm">Can add employees</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={managerPermissions.can_edit_employees}
                        onCheckedChange={(checked) => setManagerPermissions({ ...managerPermissions, can_edit_employees: checked })}
                      />
                      <label className="text-sm">Can edit employees</label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        checked={managerPermissions.can_delete_employees}
                        onCheckedChange={(checked) => setManagerPermissions({ ...managerPermissions, can_delete_employees: checked })}
                      />
                      <label className="text-sm">Can delete employees</label>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddAdmin(false)}>Cancel</Button>
              <Button onClick={handleAddAdmin}>Create Admin</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Password</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Current Password</Label>
                <Input
                  type="password"
                  value={passwordData.current}
                  onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>New Password</Label>
                <Input
                  type="password"
                  value={passwordData.new}
                  onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Confirm New Password</Label>
                <Input
                  type="password"
                  value={passwordData.confirm}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowChangePassword(false)}>Cancel</Button>
              <Button onClick={handleChangePassword}>Change Password</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Admin Dialog */}
        <Dialog open={showEditAdmin} onOpenChange={setShowEditAdmin}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Admin - {editingAdmin?.username}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Username</Label>
                <Input
                  value={editAdminData.username}
                  onChange={(e) => setEditAdminData({ ...editAdminData, username: e.target.value })}
                  placeholder="Enter username"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={editAdminData.email}
                  onChange={(e) => setEditAdminData({ ...editAdminData, email: e.target.value })}
                  placeholder="Enter email (for alerts)"
                  className="mt-1"
                />
              </div>
              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                <p><strong>Role:</strong> {getRoleLabel(editingAdmin?.role)}</p>
                <p className="text-xs mt-1">To change password, use the "Change Password" button on your own account.</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditAdmin(false)}>Cancel</Button>
              <Button onClick={handleSaveAdminEdit}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

// Admin Dashboard
const AdminDashboard = ({ currentAdmin, onLogout, onAdminUpdate }) => {
  const [stats, setStats] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPayPeriod, setSelectedPayPeriod] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const navigate = useNavigate();

  // Permissions
  const canEditEntries = currentAdmin?.permissions?.can_edit_entries;
  const canDeleteEntries = currentAdmin?.permissions?.can_delete_entries;
  const canAddEmployees = currentAdmin?.permissions?.can_add_employees;
  const canEditEmployees = currentAdmin?.permissions?.can_edit_employees;
  const canDeleteEmployees = currentAdmin?.permissions?.can_delete_employees;

  // Dialog states
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showEditEmployee, setShowEditEmployee] = useState(false);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showEditEntry, setShowEditEntry] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);

  // Form states
  const [newEmployee, setNewEmployee] = useState({ name: "", pin: "", email: "" });
  const [editEmployee, setEditEmployee] = useState({ name: "", pin: "", email: "" });
  const [manualEntry, setManualEntry] = useState({ clockIn: "", clockOut: "" });
  const [editEntryData, setEditEntryData] = useState({ clockIn: "", clockOut: "" });

  // Payroll states
  const [showPayrollPreview, setShowPayrollPreview] = useState(false);
  const [payrollData, setPayrollData] = useState(null);
  const [payrollStatus, setPayrollStatus] = useState(null);
  const [isSubmittingPayroll, setIsSubmittingPayroll] = useState(false);

  // Payroll Lock/Unlock states
  const [showUnlockDialog, setShowUnlockDialog] = useState(false);
  const [unlockData, setUnlockData] = useState({ reasonCode: '', detailedNote: '' });
  const [unlockReasons, setUnlockReasons] = useState({});

  // Audit & Roster states
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showInactiveEmployees, setShowInactiveEmployees] = useState(false);
  const [inactiveEmployees, setInactiveEmployees] = useState([]);
  const [showClockedInRoster, setShowClockedInRoster] = useState(false);
  const [clockedInRoster, setClockedInRoster] = useState({ count: 0, roster: [] });

  // Holiday Pay states
  const [showHolidayPay, setShowHolidayPay] = useState(false);
  const [holidayPayData, setHolidayPayData] = useState({
    holidayName: '',
    date: new Date().toISOString().split('T')[0],
    selectedEmployees: []
  });

  // Fetch unlock reasons on mount
  useEffect(() => {
    const fetchUnlockReasons = async () => {
      try {
        const response = await axios.get(`${API}/payroll/unlock-reasons`);
        setUnlockReasons(response.data);
      } catch (error) {
        console.error("Failed to fetch unlock reasons", error);
      }
    };
    fetchUnlockReasons();
  }, []);

  useEffect(() => {
    const fetchPayPeriods = async () => {
      try {
        const response = await axios.get(`${API}/pay-periods`);
        setPayPeriods(response.data);
        const current = response.data.find(p => p.is_current);
        if (current) setSelectedPayPeriod(current);
      } catch (error) {
        console.error("Failed to fetch pay periods", error);
      }
    };
    fetchPayPeriods();
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedPayPeriod && !startDate && !endDate) {
        const startParts = selectedPayPeriod.start_formatted.split('/');
        const endParts = selectedPayPeriod.end_formatted.split('/');
        params.append("start_date", `${startParts[2]}-${startParts[0]}-${startParts[1]}`);
        params.append("end_date", `${endParts[2]}-${endParts[0]}-${endParts[1]}`);
      } else {
        if (startDate) params.append("start_date", format(startDate, "yyyy-MM-dd"));
        if (endDate) params.append("end_date", format(endDate, "yyyy-MM-dd"));
      }

      const response = await axios.get(`${API}/stats?${params.toString()}`);
      setStats(response.data);
      setEmployees(response.data.employees);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, selectedPayPeriod]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handlePayPeriodChange = (periodLabel) => {
    const period = payPeriods.find(p => p.label === periodLabel);
    if (period) {
      setSelectedPayPeriod(period);
      setStartDate(null);
      setEndDate(null);
    }
  };

  const handleAddEmployee = async () => {
    // Validate all fields are filled
    if (!newEmployee.name.trim()) {
      toast.error("Please enter employee name");
      return;
    }
    if (!newEmployee.pin.trim()) {
      toast.error("Please enter employee PIN");
      return;
    }
    if (!newEmployee.email.trim()) {
      toast.error("Please enter employee email");
      return;
    }
    
    try {
      const payload = { 
        name: newEmployee.name, 
        pin: newEmployee.pin,
        email: newEmployee.email
      };
      await axios.post(`${API}/employees`, payload);
      toast.success("Employee added successfully");
      setShowAddEmployee(false);
      setNewEmployee({ name: "", pin: "", email: "" });
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add employee");
    }
  };

  const handleEditEmployee = async () => {
    try {
      const payload = {};
      if (editEmployee.name) payload.name = editEmployee.name;
      if (editEmployee.pin) payload.pin = editEmployee.pin;
      if (editEmployee.email !== undefined) payload.email = editEmployee.email;
      
      await axios.put(`${API}/employees/${selectedEmployee.id}?admin_username=${encodeURIComponent(currentAdmin?.username || '')}`, payload);
      toast.success("Employee updated successfully");
      setShowEditEmployee(false);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update employee");
    }
  };

  const handleDeactivateEmployee = async (employeeId, employeeName) => {
    let note = null;
    while (!note || note.trim() === '') {
      note = window.prompt(`Deactivating ${employeeName}. Enter a reason (required):`);
      if (note === null) return; // User cancelled
      if (!note.trim()) {
        alert("A reason is required to deactivate an employee.");
      }
    }
    
    try {
      await axios.post(`${API}/employees/${employeeId}/deactivate?admin_username=${encodeURIComponent(currentAdmin?.username || '')}&note=${encodeURIComponent(note)}`);
      toast.success("Employee deactivated successfully");
      fetchStats();
    } catch (error) {
      toast.error("Failed to deactivate employee");
    }
  };

  const handleViewHistory = async (employee) => {
    setSelectedEmployee(employee);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append("start_date", format(startDate, "yyyy-MM-dd"));
      if (endDate) params.append("end_date", format(endDate, "yyyy-MM-dd"));

      const response = await axios.get(`${API}/time-entries/${employee.id}?${params.toString()}`);
      setTimeEntries(response.data);
      setShowHistory(true);
    } catch (error) {
      toast.error("Failed to fetch time entries");
    }
  };

  const handleAddManualEntry = async (clockInOnly = false) => {
    const note = window.prompt(clockInOnly 
      ? "Enter a reason for clocking in this employee (optional):" 
      : "Enter a reason for this manual entry (optional):");
    if (note === null) return; // User cancelled
    
    try {
      const payload = {
        employee_id: selectedEmployee.id,
        clock_in: new Date(manualEntry.clockIn).toISOString(),
        note: note || null
      };
      
      // Only include clock_out if it's provided and not clock-in-only mode
      if (!clockInOnly && manualEntry.clockOut) {
        payload.clock_out = new Date(manualEntry.clockOut).toISOString();
      }
      
      await axios.post(`${API}/time-entries/manual?admin_username=${encodeURIComponent(currentAdmin?.username || '')}`, payload);
      toast.success(clockInOnly ? "Employee clocked in successfully" : "Entry added successfully");
      setShowAddEntry(false);
      setManualEntry({ clockIn: "", clockOut: "" });
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add entry");
    }
  };

  const handleDeleteEntry = async (entryId) => {
    const note = window.prompt("Enter a reason for deleting this entry (optional):");
    if (note === null) return;
    
    try {
      await axios.delete(`${API}/time-entries/${entryId}?admin_username=${encodeURIComponent(currentAdmin?.username || '')}&note=${encodeURIComponent(note || '')}`);
      toast.success("Entry deleted successfully");
      handleViewHistory(selectedEmployee);
      fetchStats();
    } catch (error) {
      toast.error("Failed to delete entry");
    }
  };

  const handleEditEntry = (entry) => {
    setSelectedEntry(entry);
    setEditEntryData({
      clockIn: entry.clock_in ? entry.clock_in.slice(0, 16) : "",
      clockOut: entry.clock_out ? entry.clock_out.slice(0, 16) : ""
    });
    setShowEditEntry(true);
  };

  const handleSaveEntryEdit = async () => {
    const note = window.prompt("Enter a reason for this edit (optional):");
    if (note === null) return; // User cancelled
    
    try {
      await axios.put(`${API}/time-entries/${selectedEntry.id}?admin_username=${encodeURIComponent(currentAdmin?.username || '')}`, {
        clock_in: editEntryData.clockIn ? new Date(editEntryData.clockIn).toISOString() : null,
        clock_out: editEntryData.clockOut ? new Date(editEntryData.clockOut).toISOString() : null,
        note: note || null
      });
      toast.success("Entry updated successfully");
      setShowEditEntry(false);
      handleViewHistory(selectedEmployee);
      fetchStats();
    } catch (error) {
      toast.error("Failed to update entry");
    }
  };

  const handleSetSnowPayHours = async (entryId, hours, totalShiftHours) => {
    const snowHours = parseFloat(hours);
    if (isNaN(snowHours) || snowHours < 0) {
      toast.error("Please enter a valid number of hours");
      return;
    }
    if (snowHours > totalShiftHours) {
      toast.error(`Snow Pay hours cannot exceed shift hours (${totalShiftHours.toFixed(2)}h)`);
      return;
    }
    try {
      await axios.put(`${API}/time-entries/${entryId}/snow-pay?snow_pay_hours=${snowHours}&admin_username=${encodeURIComponent(currentAdmin?.username || '')}`);
      toast.success(snowHours > 0 ? `Set ${snowHours}h as Snow Pay` : "Snow Pay removed");
      handleViewHistory(selectedEmployee);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update Snow Pay hours");
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate("/");
  };

  // Payroll functions
  const checkPayrollStatus = async () => {
    if (!selectedPayPeriod) return;
    try {
      const response = await axios.get(`${API}/payroll/status/${selectedPayPeriod.key}`);
      setPayrollStatus(response.data);
    } catch (error) {
      console.error("Failed to check payroll status", error);
    }
  };

  useEffect(() => {
    if (selectedPayPeriod) {
      checkPayrollStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayPeriod]);

  const handlePreviewPayroll = async () => {
    if (!selectedPayPeriod) {
      toast.error("Please select a pay period first");
      return;
    }
    try {
      const startParts = selectedPayPeriod.start_formatted.split('/');
      const endParts = selectedPayPeriod.end_formatted.split('/');
      const response = await axios.post(`${API}/payroll/preview`, {
        pay_period_key: selectedPayPeriod.key,
        start_date: `${startParts[2]}-${startParts[0]}-${startParts[1]}`,
        end_date: `${endParts[2]}-${endParts[0]}-${endParts[1]}`
      });
      setPayrollData(response.data);
      setShowPayrollPreview(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to preview payroll");
    }
  };

  const handleSubmitPayroll = async () => {
    if (!selectedPayPeriod) return;
    setIsSubmittingPayroll(true);
    try {
      const startParts = selectedPayPeriod.start_formatted.split('/');
      const endParts = selectedPayPeriod.end_formatted.split('/');
      const response = await axios.post(`${API}/payroll/submit`, {
        pay_period_key: selectedPayPeriod.key,
        start_date: `${startParts[2]}-${startParts[0]}-${startParts[1]}`,
        end_date: `${endParts[2]}-${endParts[0]}-${endParts[1]}`
      });
      setPayrollData(response.data);
      toast.success("Payroll submitted successfully!");
      checkPayrollStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit payroll");
    } finally {
      setIsSubmittingPayroll(false);
    }
  };

  // Payroll Unlock handlers
  const openUnlockDialog = () => {
    setUnlockData({ reasonCode: '', detailedNote: '' });
    setShowUnlockDialog(true);
  };

  const handleUnlockPayroll = async () => {
    if (!unlockData.reasonCode) {
      toast.error("Please select an unlock reason");
      return;
    }
    if (unlockData.detailedNote.trim().length < 10) {
      toast.error("Please provide a detailed note (minimum 10 characters)");
      return;
    }
    if (unlockData.reasonCode === 'OTHER' && unlockData.detailedNote.trim().length < 20) {
      toast.error("'Other' reason requires a detailed note (minimum 20 characters)");
      return;
    }

    try {
      await axios.post(
        `${API}/payroll/unlock?admin_username=${encodeURIComponent(currentAdmin?.username || '')}&admin_role=${encodeURIComponent(currentAdmin?.role || '')}`,
        {
          pay_period_key: selectedPayPeriod.key,
          reason_code: unlockData.reasonCode,
          detailed_note: unlockData.detailedNote
        }
      );
      toast.success("Payroll unlocked for 4 hours - will auto-lock after");
      setShowUnlockDialog(false);
      checkPayrollStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unlock payroll");
    }
  };

  const handleRelockPayroll = async () => {
    if (!window.confirm("Are you sure you want to re-lock this payroll? Edits will no longer be allowed.")) {
      return;
    }
    try {
      await axios.post(
        `${API}/payroll/relock/${selectedPayPeriod.key}?admin_username=${encodeURIComponent(currentAdmin?.username || '')}&admin_role=${encodeURIComponent(currentAdmin?.role || '')}`
      );
      toast.success("Payroll re-locked successfully");
      checkPayrollStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to re-lock payroll");
    }
  };

  // Fetch audit logs
  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/audit-logs?limit=100`);
      setAuditLogs(response.data);
      setShowAuditLog(true);
    } catch (error) {
      toast.error("Failed to fetch audit logs");
    }
  };

  // Fetch inactive employees
  const fetchInactiveEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees/inactive`);
      setInactiveEmployees(response.data);
      setShowInactiveEmployees(true);
    } catch (error) {
      toast.error("Failed to fetch inactive employees");
    }
  };

  // Reactivate employee
  const handleReactivateEmployee = async (employeeId, employeeName) => {
    const note = window.prompt(`Reactivating ${employeeName}. Enter a reason (optional):`);
    if (note === null) return;
    
    try {
      await axios.post(`${API}/employees/${employeeId}/reactivate?admin_username=${encodeURIComponent(currentAdmin?.username || '')}&note=${encodeURIComponent(note || '')}`);
      toast.success("Employee reactivated successfully");
      fetchInactiveEmployees();
      fetchStats();
    } catch (error) {
      toast.error("Failed to reactivate employee");
    }
  };

  // Fetch clocked-in roster
  const fetchClockedInRoster = async () => {
    try {
      const response = await axios.get(`${API}/clocked-in-roster`);
      setClockedInRoster(response.data);
      setShowClockedInRoster(true);
    } catch (error) {
      toast.error("Failed to fetch clocked-in roster");
    }
  };

  // Holiday Pay handlers
  const openHolidayPayDialog = () => {
    setHolidayPayData({
      holidayName: '',
      date: new Date().toISOString().split('T')[0],
      selectedEmployees: []
    });
    setShowHolidayPay(true);
  };

  const toggleEmployeeSelection = (employeeId) => {
    setHolidayPayData(prev => ({
      ...prev,
      selectedEmployees: prev.selectedEmployees.includes(employeeId)
        ? prev.selectedEmployees.filter(id => id !== employeeId)
        : [...prev.selectedEmployees, employeeId]
    }));
  };

  const selectAllEmployees = () => {
    setHolidayPayData(prev => ({
      ...prev,
      selectedEmployees: prev.selectedEmployees.length === employees.length
        ? []
        : employees.map(emp => emp.id)
    }));
  };

  const handleSubmitHolidayPay = async () => {
    if (!holidayPayData.holidayName.trim()) {
      toast.error("Please enter a holiday name");
      return;
    }
    if (holidayPayData.selectedEmployees.length === 0) {
      toast.error("Please select at least one employee");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/time-entries/holiday-pay?admin_username=${encodeURIComponent(currentAdmin?.username || '')}`,
        {
          employee_ids: holidayPayData.selectedEmployees,
          holiday_name: holidayPayData.holidayName,
          date: holidayPayData.date,
          hours: 8.0
        }
      );
      toast.success(response.data.message);
      setShowHolidayPay(false);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add holiday pay");
    }
  };

  const filteredEmployees = employees.filter(
    (emp) =>
      emp.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.pin.includes(searchQuery)
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gray-900 text-white p-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img src={LOGO_URL} alt="Service Pro" className="h-10" />
            <div>
              <h1 className="text-xl font-bold">Time Clock Admin</h1>
              <span className="text-xs text-gray-400">
                {currentAdmin?.username} ({currentAdmin?.role?.replace("_", " ")})
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white text-sm">
              Hello, <span className="font-semibold">{currentAdmin?.username}</span>
            </span>
            <Button variant="ghost" className="text-white hover:bg-gray-800" onClick={() => setShowSettings(true)}>
              <Settings className="h-4 w-4" />
            </Button>
            <Button variant="ghost" className="text-white hover:bg-gray-800" onClick={handleLogout}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Dashboard Header */}
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Admin Dashboard</h2>
            <p className="text-gray-500">Manage employees and view clock records</p>
          </div>
          {canAddEmployees && (
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setShowAddEmployee(true)} data-testid="add-employee-btn">
              <UserPlus className="mr-2 h-4 w-4" />
              Add Employee
            </Button>
          )}
        </div>

        {/* Pay Period Selector */}
        <div className="bg-white rounded-lg border p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[250px]">
              <Label className="text-sm font-medium">Select Pay Period</Label>
              <Select value={selectedPayPeriod?.label || ""} onValueChange={handlePayPeriodChange}>
                <SelectTrigger className="w-full mt-1" data-testid="pay-period-select">
                  <SelectValue placeholder="Select a pay period" />
                </SelectTrigger>
                <SelectContent>
                  {payPeriods.map((period, index) => (
                    <SelectItem 
                      key={index} 
                      value={period.label} 
                      className={`${period.is_current ? "font-semibold bg-blue-50" : ""} ${period.is_locked ? "text-gray-600" : ""}`}
                    >
                      {period.is_locked && "🔒 "}{period.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {selectedPayPeriod && (
            <div className="mt-3 flex justify-between items-center">
              <div className="text-sm">
                <span className="text-gray-500">Viewing:</span>{" "}
                <span className={`font-medium ${selectedPayPeriod.is_current ? "text-blue-600" : "text-gray-900"}`}>
                  {selectedPayPeriod.start_formatted} - {selectedPayPeriod.end_formatted}
                  {selectedPayPeriod.is_current && " (Current)"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                {payrollStatus?.submitted && (
                  <>
                    {payrollStatus.is_locked ? (
                      <span className="text-sm text-green-600 font-medium flex items-center gap-1">
                        🔒 Payroll Submitted & Locked
                      </span>
                    ) : (
                      <span className="text-sm text-orange-600 font-medium flex items-center gap-1">
                        🔓 Unlocked by {payrollStatus.unlock_info?.unlocked_by}
                        {payrollStatus.unlock_info?.time_remaining && (
                          <span className="ml-2 text-xs bg-orange-100 px-2 py-0.5 rounded">
                            ⏱️ Auto-locks in {payrollStatus.unlock_info.time_remaining}
                          </span>
                        )}
                      </span>
                    )}
                  </>
                )}
                
                {/* Show different buttons based on payroll state */}
                {!payrollStatus?.submitted ? (
                  <Button 
                    onClick={handlePreviewPayroll}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Submit Timecards
                  </Button>
                ) : payrollStatus.is_locked ? (
                  currentAdmin?.role === 'president' && (
                    <Button 
                      onClick={openUnlockDialog}
                      variant="outline"
                      className="border-orange-400 text-orange-600 hover:bg-orange-50"
                    >
                      🔓 Unlock for Edits
                    </Button>
                  )
                ) : (
                  currentAdmin?.role === 'president' && (
                    <Button 
                      onClick={handleRelockPayroll}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      🔒 Re-lock Payroll
                    </Button>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500">Hours Today</p>
                  <p className="text-2xl font-bold">{stats?.hours_today || "0h 0m"}</p>
                </div>
                <Clock className="h-10 w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500">Hours This Pay Period</p>
                  <p className="text-2xl font-bold">{stats?.hours_pay_period || "0h 0m"}</p>
                </div>
                <Clock className="h-10 w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={fetchClockedInRoster}>
            <CardContent className="p-6">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500">Currently Clocked In</p>
                  <p className="text-2xl font-bold">{stats?.currently_clocked_in || 0}</p>
                  <p className="text-xs text-blue-500 mt-1">Click to view roster →</p>
                </div>
                <Users className="h-10 w-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchAuditLogs}>
            View Audit Log
          </Button>
          <Button variant="outline" onClick={fetchInactiveEmployees}>
            View Inactive Employees
          </Button>
          <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={openHolidayPayDialog}>
            🎉 Add Holiday Pay
          </Button>
        </div>

        {/* Employees Table */}
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                <h3 className="text-lg font-semibold">Employees ({filteredEmployees.length})</h3>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Search by name or PIN..." className="pl-10 w-64" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} data-testid="search-employees-input" />
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>PIN</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Regular Hours</TableHead>
                  <TableHead>Overtime</TableHead>
                  <TableHead>Added</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell className="font-medium">{employee.name}</TableCell>
                    <TableCell>{employee.pin}</TableCell>
                    <TableCell>
                      {employee.email ? (
                        <span className="flex items-center gap-1 text-sm text-gray-600">
                          <Mail className="h-3 w-3" />
                          {employee.email}
                        </span>
                      ) : (
                        <span className="text-gray-400 text-sm">No email</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-blue-600 font-medium">{employee.regular_hours_formatted || "0h 0m"}</span>
                    </TableCell>
                    <TableCell>
                      <span className={`font-medium ${employee.overtime_hours > 0 ? "text-orange-600" : "text-gray-400"}`}>
                        {employee.overtime_hours > 0 ? employee.overtime_formatted : "0h 0m"}
                      </span>
                    </TableCell>
                    <TableCell>{employee.added_date ? format(new Date(employee.added_date), "MM/dd/yyyy") : "N/A"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button variant="outline" size="sm" onClick={() => { setSelectedEmployee(employee); setShowAddEntry(true); }}>
                          Add Entry
                        </Button>
                        {canEditEmployees && (
                          <Button variant="outline" size="sm" onClick={() => { setSelectedEmployee(employee); setEditEmployee({ name: employee.name, pin: employee.pin, email: employee.email || "" }); setShowEditEmployee(true); }}>
                            Edit
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => handleViewHistory(employee)}>
                          View History
                        </Button>
                        {canDeleteEmployees && (
                          <Button variant="outline" size="sm" className="text-orange-600 border-orange-300 hover:bg-orange-50" onClick={() => handleDeactivateEmployee(employee.id, employee.name)}>
                            Deactivate
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Settings Dialog */}
      <SettingsDialog open={showSettings} onOpenChange={setShowSettings} currentAdmin={currentAdmin} onAdminUpdate={onAdminUpdate} />

      {/* Add Employee Dialog */}
      <Dialog open={showAddEmployee} onOpenChange={setShowAddEmployee}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Employee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="new-name">Name</Label>
              <Input id="new-name" value={newEmployee.name} onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })} placeholder="Enter employee name" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="new-pin">PIN</Label>
              <Input id="new-pin" value={newEmployee.pin} onChange={(e) => setNewEmployee({ ...newEmployee, pin: e.target.value })} placeholder="Enter PIN (e.g., 123456)" className="mt-1" maxLength={6} />
            </div>
            <div>
              <Label htmlFor="new-email">Email</Label>
              <Input id="new-email" type="email" value={newEmployee.email} onChange={(e) => setNewEmployee({ ...newEmployee, email: e.target.value })} placeholder="Enter employee email" className="mt-1" />
              <p className="text-xs text-gray-500 mt-1">Employee will receive clock-out notifications at this email</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddEmployee(false)}>Cancel</Button>
            <Button onClick={handleAddEmployee}>Add Employee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Employee Dialog */}
      <Dialog open={showEditEmployee} onOpenChange={setShowEditEmployee}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Employee</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Name</Label>
              <Input id="edit-name" value={editEmployee.name} onChange={(e) => setEditEmployee({ ...editEmployee, name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="edit-pin">PIN</Label>
              <Input id="edit-pin" value={editEmployee.pin} onChange={(e) => setEditEmployee({ ...editEmployee, pin: e.target.value })} className="mt-1" maxLength={6} />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input id="edit-email" type="email" value={editEmployee.email} onChange={(e) => setEditEmployee({ ...editEmployee, email: e.target.value })} placeholder="Enter employee email" className="mt-1" />
            </div>
          </div> 
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditEmployee(false)}>Cancel</Button>
            <Button onClick={handleEditEmployee}>Update Employee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Manual Entry Dialog */}
      <Dialog open={showAddEntry} onOpenChange={setShowAddEntry}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Entry for {selectedEmployee?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <strong>💡 Tip:</strong> Leave "Clock Out" empty to just clock someone in. They can clock out themselves later.
            </div>
            <div>
              <Label htmlFor="clock-in">Clock In *</Label>
              <Input id="clock-in" type="datetime-local" value={manualEntry.clockIn} onChange={(e) => setManualEntry({ ...manualEntry, clockIn: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="clock-out">Clock Out (optional)</Label>
              <Input id="clock-out" type="datetime-local" value={manualEntry.clockOut} onChange={(e) => setManualEntry({ ...manualEntry, clockOut: e.target.value })} className="mt-1" />
              <p className="text-xs text-gray-500 mt-1">Leave empty if employee will clock out themselves</p>
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowAddEntry(false)}>Cancel</Button>
            {!manualEntry.clockOut && manualEntry.clockIn && (
              <Button 
                onClick={() => handleAddManualEntry(true)} 
                className="bg-blue-600 hover:bg-blue-700"
              >
                Clock In Only
              </Button>
            )}
            <Button 
              onClick={() => handleAddManualEntry(false)}
              disabled={!manualEntry.clockIn || !manualEntry.clockOut}
              className="bg-green-600 hover:bg-green-700"
            >
              Add Full Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Time Entry Dialog */}
      <Dialog open={showEditEntry} onOpenChange={setShowEditEntry}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Time Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Clock In</Label>
              <Input type="datetime-local" value={editEntryData.clockIn} onChange={(e) => setEditEntryData({ ...editEntryData, clockIn: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Clock Out</Label>
              <Input type="datetime-local" value={editEntryData.clockOut} onChange={(e) => setEditEntryData({ ...editEntryData, clockOut: e.target.value })} className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditEntry(false)}>Cancel</Button>
            <Button onClick={handleSaveEntryEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <div className="flex justify-between items-center">
              <DialogTitle>Clock History - {selectedEmployee?.name}</DialogTitle>
              <span className="text-blue-600 font-semibold">Total: {selectedEmployee?.total_hours_formatted}</span>
            </div>
          </DialogHeader>
          <div className="max-h-[65vh] overflow-auto space-y-4 pr-2">
            {timeEntries.map((entry) => {
              const clockIn = new Date(entry.clock_in);
              const clockOut = entry.clock_out ? new Date(entry.clock_out) : null;
              // For holiday entries, use stored holiday_hours; for regular entries, calculate from clock times
              const hours = entry.is_holiday 
                ? (entry.holiday_hours || 8).toFixed(2)
                : (clockOut ? ((clockOut - clockIn) / 3600000).toFixed(2) : null);

              // Holiday pay entries display differently
              if (entry.is_holiday) {
                return (
                  <div key={entry.id} className="border-2 border-purple-200 bg-purple-50 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <span className="bg-purple-600 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          🎉 HOLIDAY PAY
                        </span>
                        <span className="font-semibold text-purple-800">{entry.holiday_name}</span>
                      </div>
                      <div className="flex gap-2">
                        {canDeleteEntries && (
                          <Button variant="destructive" size="sm" onClick={() => handleDeleteEntry(entry.id)}>Delete</Button>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex justify-between items-center">
                      <div className="text-gray-600">
                        <Clock className="h-4 w-4 inline mr-2" />
                        {format(clockIn, "MM/dd/yyyy")}
                      </div>
                      <div className="text-lg font-bold text-purple-700">
                        {hours}h Holiday Pay
                      </div>
                    </div>
                    {entry.created_by && (
                      <div className="mt-2 text-xs text-gray-500">Added by {entry.created_by}</div>
                    )}
                  </div>
                );
              }

              return (
                <div key={entry.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex gap-4">
                    {entry.photo_in ? (
                      <img src={entry.photo_in} alt="Clock In" className="w-24 h-24 object-cover rounded-lg" />
                    ) : (
                      <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                        <Camera className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <span className="bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">Clock IN</span>
                        <div className="flex gap-2">
                          {canEditEntries && (
                            <Button variant="outline" size="sm" onClick={() => handleEditEntry(entry)}>
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                          {canDeleteEntries && (
                            <Button variant="destructive" size="sm" onClick={() => handleDeleteEntry(entry.id)}>Delete</Button>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2 text-gray-600">
                        <Clock className="h-4 w-4" />
                        {formatToEasternTime(entry.clock_in)}
                      </div>
                      {entry.location_in && (
                        <div className="mt-1 flex items-start gap-2 text-gray-600 text-sm">
                          <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <a href={`https://maps.google.com/?q=${entry.location_in.lat},${entry.location_in.lng}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 underline">
                            {entry.location_in.address || "View on Map"}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {clockOut && (
                    <div className="flex gap-4 border-t pt-3">
                      {entry.photo_out ? (
                        <img src={entry.photo_out} alt="Clock Out" className="w-24 h-24 object-cover rounded-lg" />
                      ) : (
                        <div className="w-24 h-24 bg-gray-200 rounded-lg flex items-center justify-center">
                          <Camera className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1">
                        <span className="bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">Clock OUT</span>
                        <div className="mt-2 flex items-center gap-2 text-gray-600">
                          <Clock className="h-4 w-4" />
                          {formatToEasternTime(entry.clock_out)}
                        </div>
                        {entry.location_out && (
                          <div className="mt-1 flex items-start gap-2 text-gray-600 text-sm">
                            <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                            <a href={`https://maps.google.com/?q=${entry.location_out.lat},${entry.location_out.lng}`} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 underline">
                              {entry.location_out.address || "View on Map"}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {hours && (
                    <div className="flex justify-between items-center text-sm text-gray-500 border-t pt-2">
                      <div className="flex flex-wrap gap-2 items-center">
                        {entry.is_holiday && (
                          <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded text-xs">
                            🎉 Holiday: {entry.holiday_name}
                          </span>
                        )}
                        {entry.snow_pay_hours > 0 && (
                          <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                            ❄️ Snow Pay: {entry.snow_pay_hours}h
                          </span>
                        )}
                        {entry.is_manual && !entry.is_holiday && (
                          <span className="text-orange-500 text-xs">(Manual Entry)</span>
                        )}
                        {(entry.created_by || entry.last_edited_by) && (
                          <span className="text-gray-400 text-xs">
                            {entry.last_edited_by 
                              ? `Edited by ${entry.last_edited_by}`
                              : entry.created_by 
                                ? `Added by ${entry.created_by}`
                                : ''
                            }
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        {/* Snow Pay Hours Input */}
                        {canEditEntries && !entry.is_holiday && (
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500">❄️ Snow:</span>
                            <input
                              type="number"
                              min="0"
                              max={hours}
                              step="0.5"
                              defaultValue={entry.snow_pay_hours || 0}
                              className="w-16 px-2 py-1 text-xs border rounded text-center"
                              onBlur={(e) => {
                                const newHours = parseFloat(e.target.value) || 0;
                                const currentHours = entry.snow_pay_hours || 0;
                                if (newHours !== currentHours) {
                                  handleSetSnowPayHours(entry.id, newHours, parseFloat(hours));
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                }
                              }}
                            />
                            <span className="text-xs text-gray-400">h</span>
                          </div>
                        )}
                        <div>
                          Hours: <span className="font-semibold text-gray-700">{hours}h</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {!clockOut && (
                    <div className="text-right text-sm text-green-600 border-t pt-2 font-semibold">Currently Clocked In</div>
                  )}
                </div>
              );
            })}
            {timeEntries.length === 0 && (
              <div className="text-center text-gray-500 py-8">No time entries found</div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timecard Preview Dialog */}
      <Dialog open={showPayrollPreview} onOpenChange={setShowPayrollPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Timecard Preview - {payrollData?.pay_period_key}</DialogTitle>
          </DialogHeader>
          {payrollData && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">
                  <strong>Pay Period:</strong> {payrollData.start_date} - {payrollData.end_date}
                </p>
              </div>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="p-3 text-left">Employee</th>
                      <th className="p-3 text-center">Regular Time</th>
                      <th className="p-3 text-center">Overtime</th>
                      <th className="p-3 text-center">Holiday</th>
                      <th className="p-3 text-center">Snow Pay</th>
                      <th className="p-3 text-center">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payrollData.employees?.map((emp, idx) => (
                      <tr key={idx} className="border-b">
                        <td className="p-3">{emp.name}</td>
                        <td className="p-3 text-center">{emp.regular_hours_formatted}</td>
                        <td className="p-3 text-center">{emp.overtime_hours_formatted}</td>
                        <td className="p-3 text-center">{emp.holiday_hours_formatted || "0:00"}</td>
                        <td className="p-3 text-center">{emp.snow_pay_hours_formatted || "0:00"}</td>
                        <td className="p-3 text-center font-bold">{emp.total_hours_formatted}</td>
                      </tr>
                    ))}
                    <tr className="bg-green-50 font-bold">
                      <td className="p-3">TOTALS</td>
                      <td className="p-3 text-center">{payrollData.totals?.regular_hours_formatted}</td>
                      <td className="p-3 text-center">{payrollData.totals?.overtime_hours_formatted}</td>
                      <td className="p-3 text-center">{payrollData.totals?.holiday_hours_formatted || "0:00"}</td>
                      <td className="p-3 text-center">{payrollData.totals?.snow_pay_hours_formatted || "0:00"}</td>
                      <td className="p-3 text-center text-green-600 text-lg">{payrollData.totals?.total_hours_formatted}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {payrollData.preview_only && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Note:</strong> This is a preview. Click "Confirm & Submit" to finalize this timecard.
                  </p>
                  <p className="text-xs text-yellow-700 mt-2 border-t border-yellow-200 pt-2">
                    🔒 Once submitted, this timecard will be <strong>locked</strong>. Only Casey can unlock it with a valid reason for corrections.
                  </p>
                </div>
              )}

              {payrollData.submitted_at && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-sm text-green-800">
                    ✓ Timecard submitted successfully on {formatToEasternTime(payrollData.submitted_at)}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayrollPreview(false)}>
              {payrollData?.preview_only ? "Cancel" : "Close"}
            </Button>
            {payrollData?.preview_only && (
              <Button 
                onClick={handleSubmitPayroll} 
                disabled={isSubmittingPayroll}
                className="bg-green-600 hover:bg-green-700"
              >
                {isSubmittingPayroll ? "Submitting..." : "Confirm & Submit"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={showAuditLog} onOpenChange={setShowAuditLog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Log</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {auditLogs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No audit logs found</p>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {auditLogs.map((log, idx) => (
                  <div key={idx} className="border rounded-lg p-3 bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div>
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          log.action_type === 'MANUAL_ENTRY' ? 'bg-blue-100 text-blue-800' :
                          log.action_type === 'PUNCH_EDIT' ? 'bg-yellow-100 text-yellow-800' :
                          log.action_type === 'PAY_RATE_CHANGE' ? 'bg-green-100 text-green-800' :
                          log.action_type === 'EMPLOYEE_DEACTIVATE' ? 'bg-red-100 text-red-800' :
                          log.action_type === 'EMPLOYEE_REACTIVATE' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {log.action_label}
                        </span>
                        <span className="ml-2 font-medium">{log.target_name}</span>
                      </div>
                      <span className="text-xs text-gray-500">
                        {formatToEasternTime(log.timestamp)}
                      </span>
                    </div>
                    <div className="mt-2 text-sm">
                      <span className="text-gray-600">By: {log.performed_by}</span>
                      {log.note && (
                        <p className="text-gray-500 italic mt-1">Note: {log.note}</p>
                      )}
                      {log.changes && Object.keys(log.changes).length > 0 && (
                        <div className="mt-1 text-xs bg-white p-2 rounded border">
                          {Object.entries(log.changes).map(([key, value]) => (
                            <div key={key}>
                              <strong>{key}:</strong> {
                                typeof value === 'object' && value !== null && 'from' in value
                                  ? `${value.from ?? 'empty'} → ${value.to ?? 'empty'}`
                                  : typeof value === 'string' ? value : JSON.stringify(value)
                              }
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowAuditLog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inactive Employees Dialog */}
      <Dialog open={showInactiveEmployees} onOpenChange={setShowInactiveEmployees}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Inactive Employees</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {inactiveEmployees.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No inactive employees</p>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {inactiveEmployees.map((emp) => (
                  <div key={emp.id} className="border rounded-lg p-3 flex justify-between items-center">
                    <div>
                      <p className="font-medium">{emp.name}</p>
                      <p className="text-sm text-gray-500">PIN: {emp.pin}</p>
                      {emp.deactivated_at && (
                        <p className="text-xs text-gray-400">
                          Deactivated: {new Date(emp.deactivated_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Button size="sm" onClick={() => handleReactivateEmployee(emp.id, emp.name)}>
                      Reactivate
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowInactiveEmployees(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clocked In Roster Dialog */}
      <Dialog open={showClockedInRoster} onOpenChange={setShowClockedInRoster}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Currently Clocked In ({clockedInRoster.count})</DialogTitle>
          </DialogHeader>
          
          {/* Legend */}
          <div className="bg-gray-100 rounded-lg p-3 mb-2">
            <p className="text-xs font-semibold text-gray-600 mb-2">OVERTIME RISK LEGEND:</p>
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-green-500"></span>
                <span>Under 8hrs (OK)</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-yellow-500"></span>
                <span>8-10hrs (Monitor)</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-orange-500"></span>
                <span>10-12hrs (Warning)</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded bg-red-500"></span>
                <span>Over 12hrs (Critical)</span>
              </span>
            </div>
          </div>

          <div className="space-y-2">
            {clockedInRoster.roster.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No employees currently clocked in</p>
            ) : (
              <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                {clockedInRoster.roster.map((emp) => (
                  <div key={emp.employee_id} className={`border rounded-lg p-3 ${
                    emp.overtime_risk === 'critical' ? 'border-red-500 bg-red-50' :
                    emp.overtime_risk === 'high' ? 'border-orange-400 bg-orange-50' :
                    emp.overtime_risk === 'medium' ? 'border-yellow-400 bg-yellow-50' :
                    'border-green-400 bg-green-50'
                  }`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">{emp.name}</p>
                        <p className="text-sm text-gray-600">
                          Clocked in: {formatToEasternTime(emp.clock_in_time)}
                        </p>
                        {emp.location_in?.address && (
                          <p className="text-xs text-gray-500">{emp.location_in.address}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${
                          emp.overtime_risk === 'critical' ? 'text-red-600' :
                          emp.overtime_risk === 'high' ? 'text-orange-600' :
                          emp.overtime_risk === 'medium' ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {emp.hours_formatted}
                        </p>
                        <span className={`text-xs px-2 py-1 rounded ${
                          emp.overtime_risk === 'critical' ? 'bg-red-600 text-white' :
                          emp.overtime_risk === 'high' ? 'bg-orange-500 text-white' :
                          emp.overtime_risk === 'medium' ? 'bg-yellow-500 text-white' :
                          'bg-green-500 text-white'
                        }`}>
                          {emp.overtime_risk === 'critical' ? '⚠️ Over 12hrs!' :
                           emp.overtime_risk === 'high' ? '⚠️ Approaching 12hrs' :
                           emp.overtime_risk === 'medium' ? '⏰ Over 8hrs' :
                           '✓ Under 8hrs'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowClockedInRoster(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payroll Unlock Dialog */}
      <Dialog open={showUnlockDialog} onOpenChange={setShowUnlockDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>🔓 Unlock Payroll for Edits</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Payroll Unlock Policy */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">📋 Payroll Unlock Policy</h4>
              <div className="text-sm text-blue-800 space-y-2">
                <p><strong>Purpose:</strong> Payroll periods are locked after submission to protect the integrity of payroll records, prevent accidental changes, and ensure accurate reporting and audit readiness.</p>
                <p className="font-medium">Policy Summary:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Locked pay periods should remain locked</li>
                  <li>Unlocking is allowed only when necessary to correct a legitimate payroll-impacting error or omission</li>
                  <li>Unlocking triggers additional audit logging and requires an approved reason and written explanation</li>
                </ul>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <strong>⚠️ Warning:</strong> Unlocking payroll should only be done for legitimate corrections. 
                All changes will be logged in the audit trail.
              </p>
            </div>

            <div>
              <Label htmlFor="unlock-reason">Reason for Unlock *</Label>
              <select
                id="unlock-reason"
                className="w-full mt-1 p-2 border rounded-md"
                value={unlockData.reasonCode}
                onChange={(e) => setUnlockData(prev => ({ ...prev, reasonCode: e.target.value }))}
              >
                <option value="">Select a reason...</option>
                {Object.entries(unlockReasons).map(([code, label]) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="unlock-note">Detailed Note *</Label>
              <textarea
                id="unlock-note"
                className="w-full mt-1 p-2 border rounded-md h-24"
                value={unlockData.detailedNote}
                onChange={(e) => setUnlockData(prev => ({ ...prev, detailedNote: e.target.value }))}
                placeholder="Describe the specific correction needed (e.g., 'Correcting missing clock-out for John D. on 12/29 — confirmed by foreman')"
              />
              <p className="text-xs text-gray-500 mt-1">
                {unlockData.detailedNote.length}/10 characters minimum
                {unlockData.reasonCode === 'OTHER' && ' (20 minimum for "Other")'}
              </p>
            </div>

            <div className="bg-gray-50 border rounded-lg p-3">
              <p className="text-sm text-gray-600">
                <strong>⏱️ Auto-locks in:</strong> 4 hours<br/>
                <strong>Pay Period:</strong> {selectedPayPeriod?.label}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                The payroll will automatically re-lock after 4 hours. You can also manually re-lock it sooner.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUnlockDialog(false)}>Cancel</Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={handleUnlockPayroll}
              disabled={!unlockData.reasonCode || unlockData.detailedNote.trim().length < 10}
            >
              Unlock Payroll
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Holiday Pay Dialog */}
      <Dialog open={showHolidayPay} onOpenChange={setShowHolidayPay}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>🎉 Add Holiday Pay (8 Hours)</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="holiday-name">Holiday Name</Label>
                <Input
                  id="holiday-name"
                  value={holidayPayData.holidayName}
                  onChange={(e) => setHolidayPayData(prev => ({ ...prev, holidayName: e.target.value }))}
                  placeholder="e.g., Christmas, New Year's Day"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="holiday-date">Date</Label>
                <Input
                  id="holiday-date"
                  type="date"
                  value={holidayPayData.date}
                  onChange={(e) => setHolidayPayData(prev => ({ ...prev, date: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Select Employees</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={selectAllEmployees}
                >
                  {holidayPayData.selectedEmployees.length === employees.length ? 'Deselect All' : 'Select All'}
                </Button>
              </div>
              <div className="border rounded-lg max-h-64 overflow-y-auto">
                {employees.map((emp) => (
                  <div
                    key={emp.id}
                    className={`flex items-center p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                      holidayPayData.selectedEmployees.includes(emp.id) ? 'bg-purple-50' : ''
                    }`}
                    onClick={() => toggleEmployeeSelection(emp.id)}
                  >
                    <input
                      type="checkbox"
                      checked={holidayPayData.selectedEmployees.includes(emp.id)}
                      onChange={() => {}}
                      className="mr-3 h-4 w-4 text-purple-600"
                    />
                    <span className="font-medium">{emp.name}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {holidayPayData.selectedEmployees.length} employee(s) selected
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-800">
                <strong>Note:</strong> This will add 8 hours of holiday pay for each selected employee on {holidayPayData.date || 'the selected date'}.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHolidayPay(false)}>Cancel</Button>
            <Button 
              className="bg-purple-600 hover:bg-purple-700"
              onClick={handleSubmitHolidayPay}
              disabled={!holidayPayData.holidayName || holidayPayData.selectedEmployees.length === 0}
            >
              Add Holiday Pay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toaster position="top-center" />
    </div>
  );
};

// Admin Page with Auth
const AdminPage = () => {
  const [currentAdmin, setCurrentAdmin] = useState(null);

  const handleAdminUpdate = (updatedAdmin) => {
    setCurrentAdmin(prev => ({ ...prev, ...updatedAdmin }));
  };

  if (!currentAdmin) {
    return <AdminLogin onLogin={(admin) => setCurrentAdmin(admin)} />;
  }

  return <AdminDashboard currentAdmin={currentAdmin} onLogout={() => setCurrentAdmin(null)} onAdminUpdate={handleAdminUpdate} />;
};

// Main App with Routes
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<EmployeePage />} />
          <Route path="/admin" element={<AdminPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
